import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import V2Shell from './components/V2Shell';
import {
    EventContextProvider,
    useEventContext,
} from './components/EventContext';
import {
    IconOverview,
    IconGallery,
    IconCapture,
    IconMedia,
    IconShare,
    IconProcessing,
    IconMore,
    IconBack,
} from './components/icons';

/*
 * Event-level chrome. Provides EventContext (one resolution of
 * activation / first gallery / domain / capabilities shared by every
 * event page — see components/EventContext.tsx). Wayfinding: the Jump
 * menu owns quick-nav and the dock's back chevron climbs to the parent
 * job when the activation belongs to one. The event dock hosts the
 * event's surfaces; the page owns the event title.
 */

function EventShell() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { activationId, activation } = useEventContext();
    const base = `/v2/activations/${activationId}`;
    const jobId = activation?.jobId ?? null;

    return (
        <V2Shell
            back={{
                key: 'back',
                title: jobId ? 'Back to job' : 'Back to activations',
                icon: <IconBack />,
                onClick: () =>
                    navigate(
                        jobId ? `/v2/jobs/${jobId}` : '/v2/activations',
                    ),
            }}
            items={[
                {
                    key: 'overview',
                    title: 'Overview',
                    icon: <IconOverview />,
                    active: pathname === base,
                    onClick: () => navigate(base),
                },
                {
                    key: 'gallery',
                    title: 'Gallery',
                    icon: <IconGallery />,
                    active: pathname === `${base}/gallery`,
                    onClick: () => navigate(`${base}/gallery`),
                },
                {
                    key: 'sharing',
                    title: 'Sharing',
                    icon: <IconShare />,
                    active: pathname.startsWith(`${base}/sharing`),
                    onClick: () => navigate(`${base}/sharing`),
                },
                {
                    key: 'capture',
                    title: 'Capture Experience',
                    icon: <IconCapture />,
                    active: pathname === `${base}/capture`,
                    onClick: () => navigate(`${base}/capture`),
                },
                {
                    key: 'processing',
                    title: 'Processing',
                    icon: <IconProcessing />,
                    active: pathname.startsWith(`${base}/processing`),
                    onClick: () => navigate(`${base}/processing`),
                },
                {
                    key: 'media',
                    title: 'Media',
                    icon: <IconMedia />,
                    active: pathname === `${base}/media`,
                    onClick: () => navigate(`${base}/media`),
                },
            ]}
            actions={[
                {
                    key: 'more',
                    title: 'Everything else (classic dashboard)',
                    icon: <IconMore />,
                    onClick: () => navigate(`/activations/${activationId}`),
                },
            ]}
        />
    );
}

export default function V2EventChrome() {
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    return (
        <EventContextProvider activationId={id} key={id}>
            <EventShell />
        </EventContextProvider>
    );
}
